import React, { useState, useEffect } from 'react';
import { db, auth, functions } from './firebase'; 
import { httpsCallable } from 'firebase/functions'; 
import { doc, onSnapshot } from 'firebase/firestore';
import * as Icons from 'react-icons/fa';

/**
 * EmbeddedSetup Component - Tesla CRM
 * Upgraded Pro UI & Coexistence (WhatsApp Business App) Enabled
 */
const EmbeddedSetup = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);
  
  // API Data
  const [phoneId, setPhoneId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [metaData, setMetaData] = useState({ number: "", status: "", quality: "" });
  
  // UI States
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // --- UI HELPERS ---
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 5000);
  };

  const getQualityStyle = (quality) => {
    if (quality === "GREEN") return { bg: '#dcfce7', text: '#16a34a', icon: <Icons.FaCheckCircle /> };
    if (quality === "YELLOW") return { bg: '#fef3c7', text: '#d97706', icon: <Icons.FaExclamationTriangle /> };
    return { bg: '#fee2e2', text: '#dc2626', icon: <Icons.FaTimesCircle /> };
  };

  // --- DATABASE LISTENER ---
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const unsubscribe = onSnapshot(doc(db, "businesses", user.uid), (docSnap) => {
      if (docSnap.exists() && docSnap.data().phoneId && docSnap.data().accessToken) {
        setPhoneId(docSnap.data().phoneId);
        setAccessToken(docSnap.data().accessToken);
        setIsSetupComplete(true);
        if(!metaData.number) fetchMetaStatus(docSnap.data().phoneId, docSnap.data().accessToken);
      }
    });
    
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    
    return () => {
      unsubscribe();
      window.removeEventListener('resize', handleResize);
    };
  }, [metaData.number]);

  // --- 1. FACEBOOK SDK INITIALIZATION ---
  useEffect(() => {
    if (document.getElementById('facebook-jssdk')) { setIsSdkLoaded(true); return; }
    window.fbAsyncInit = function() {
      window.FB.init({
        appId      : '902227132534912',
        cookie     : true,
        xfbml      : true,
        version    : 'v24.0'
      });
      setIsSdkLoaded(true);
    };
    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  const fetchMetaStatus = async (pId, token) => {
    try {
      const response = await fetch(`https://graph.facebook.com/v24.0/${pId}?fields=display_phone_number,quality_rating,status,name&access_token=${token}`);
      const data = await response.json();
      if (data.display_phone_number) {
        setMetaData({ number: data.display_phone_number, status: "CONNECTED", quality: data.quality_rating || "GREEN" });
      }
    } catch (error) { console.error(error); }
  };

  // --- 2. THE MAGIC LOGIN FUNCTION (COEXISTENCE ENABLED) ---
  const launchWhatsAppSignup = () => {
    if (!window.FB) return showToast('Meta SDK still loading, please wait...', 'error');
    setLoading(true);
    
    const CONFIG_ID = '28845257335060925';

    window.FB.login(
      function (response) {
        if (response.authResponse && response.authResponse.code) {
          exchangeCodeForToken(response.authResponse.code);
        } else {
          setLoading(false);
          showToast('Login cancelled or interrupted.', 'error');
        }
      },
      {
        config_id: CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          sessionInfoVersion: '3',
          // ========================================================
          // THIS IS THE MAGIC LINE FOR "CONNECT EXISTING APP"
          // ========================================================
          featureType: 'whatsapp_business_app_onboarding' 
        }
      }
    );
  };

  const exchangeCodeForToken = async (code) => {
    try {
      const exchangeFn = httpsCallable(functions, 'exchangeMetaCode'); 
      const result = await exchangeFn({ oauthCode: code });
      if (result.data && result.data.success) {
        showToast('🎉 Connection Successful!', 'success');
      }
    } catch (error) { 
      showToast('Connection failed on server.', 'error'); 
    } 
    finally { setLoading(false); }
  };

  // --- CSS Animations ---
  const customStyles = `
    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
    .glass-panel { background: white; border: 1px solid #e2e8f0; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05); }
    .hover-lift { transition: transform 0.2s, box-shadow 0.2s; }
    .hover-lift:hover { transform: translateY(-3px); box-shadow: 0 15px 20px -3px rgba(0,0,0,0.1); }
  `;

  return (
    <div style={{ fontFamily: '"Inter", "Helvetica Neue", sans-serif', maxWidth: '1000px', margin: '0 auto', padding: isMobile ? '15px' : '30px', background: '#f8fafc', minHeight: '100vh', borderRadius: '20px' }}>
      <style>{customStyles}</style>

      {/* TOAST NOTIFICATION */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', background: toast.type === 'error' ? '#ef4444' : '#10b981', color: 'white', padding: '15px 25px', borderRadius: '10px', zIndex: 9999, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', animation: 'slideDown 0.3s ease-out' }}>
          {toast.type === 'success' ? <Icons.FaCheckCircle size={20} /> : <Icons.FaExclamationCircle size={20} />}
          {toast.message}
        </div>
      )}

      {/* HEADER */}
      <div style={{ marginBottom: '40px', animation: 'fadeIn 0.4s ease-out' }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px' }}>WhatsApp Connector</h1>
        <p style={{ margin: 0, color: '#64748b', fontSize: '16px', fontWeight: '500' }}>Direct Device & Cloud API Integration Hub</p>
      </div>

      {isSetupComplete ? (
        /* =========================================
           🟢 DASHBOARD STATE (CONNECTED)
           ========================================= */
        <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
          
          {/* Main Status Banner */}
          <div style={{ background: 'linear-gradient(135deg, #0ea5e9, #2563eb)', borderRadius: '20px', padding: '30px', color: 'white', marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', boxShadow: '0 20px 25px -5px rgba(37, 99, 235, 0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div style={{ background: 'rgba(255,255,255,0.2)', padding: '15px', borderRadius: '50%', backdropFilter: 'blur(10px)' }}>
                <Icons.FaWhatsapp size={40} color="white" />
              </div>
              <div>
                <h2 style={{ margin: '0 0 5px 0', fontSize: '24px', fontWeight: '800' }}>System Online</h2>
                <div style={{ fontSize: '14px', fontWeight: '600', opacity: 0.9, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '8px', height: '8px', background: '#6ee7b7', borderRadius: '50%' }}></div>
                  Connected to Meta API
                </div>
              </div>
            </div>
            <button onClick={() => fetchMetaStatus(phoneId, accessToken)} className="hover-lift" style={{ background: 'white', color: '#2563eb', border: 'none', padding: '12px 24px', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icons.FaSyncAlt /> Refresh Connection
            </button>
          </div>

          {/* Stats Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '25px' }}>
            
            {/* Number Card */}
            <div className="glass-panel hover-lift" style={{ padding: '25px', borderRadius: '16px', position: 'relative', overflow: 'hidden' }}>
              <Icons.FaPhoneAlt style={{ position: 'absolute', right: '-10px', bottom: '-10px', fontSize: '80px', color: '#f1f5f9', zIndex: 0 }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ color: '#64748b', fontSize: '13px', fontWeight: '700', letterSpacing: '1px', marginBottom: '10px' }}>ACTIVE NUMBER</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{metaData.number || "Loading..."}</div>
                <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#10b981', fontWeight: '600' }}>
                  <Icons.FaCheckCircle /> Meta Verified
                </div>
              </div>
            </div>

            {/* Quality Rating Card */}
            <div className="glass-panel hover-lift" style={{ padding: '25px', borderRadius: '16px', position: 'relative' }}>
              <div style={{ color: '#64748b', fontSize: '13px', fontWeight: '700', letterSpacing: '1px', marginBottom: '10px' }}>PHONE QUALITY</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: getQualityStyle(metaData.quality).bg, color: getQualityStyle(metaData.quality).text, padding: '8px 16px', borderRadius: '10px', fontSize: '18px', fontWeight: '800' }}>
                {getQualityStyle(metaData.quality).icon} {metaData.quality || "CHECKING..."}
              </div>
              <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>Determines your daily messaging limit.</p>
            </div>

            {/* API ID Card */}
            <div className="glass-panel hover-lift" style={{ padding: '25px', borderRadius: '16px', position: 'relative' }}>
              <div style={{ color: '#64748b', fontSize: '13px', fontWeight: '700', letterSpacing: '1px', marginBottom: '10px' }}>PHONE ID (API)</div>
              <div style={{ fontSize: '16px', fontFamily: 'monospace', color: '#334155', background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px dashed #cbd5e1', wordBreak: 'break-all' }}>
                {phoneId}
              </div>
            </div>

          </div>
        </div>
      ) : (
        /* =========================================
           🔵 SETUP STATE (ONBOARDING)
           ========================================= */
        <div style={{ display: 'flex', justifyContent: 'center', animation: 'fadeIn 0.5s ease-out' }}>
          <div style={{ background: 'white', padding: isMobile ? '30px 20px' : '50px', borderRadius: '24px', textAlign: 'center', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1)', width: '100%', maxWidth: '500px', border: '1px solid #f1f5f9' }}>
            
            <div style={{ width: '80px', height: '80px', background: '#eff6ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 25px' }}>
               <Icons.FaFacebook size={40} color="#1877f2" />
            </div>
            
            <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', margin: '0 0 15px 0', letterSpacing: '-0.5px' }}>
              Connect WhatsApp Business
            </h2>
            
            <p style={{ color: '#64748b', fontSize: '16px', margin: '0 auto 35px', lineHeight: '1.6', fontWeight: '500' }}>
              You can now link your existing WhatsApp Business App directly to Tesla CRM without deleting it from your phone.
            </p>
            
            <button 
              onClick={launchWhatsAppSignup} 
              disabled={!isSdkLoaded || loading} 
              className="hover-lift"
              style={{ 
                background: '#1877f2', 
                color: 'white', 
                border: 'none', 
                padding: '18px 20px', 
                borderRadius: '14px', 
                fontSize: '16px', 
                fontWeight: '700', 
                cursor: isSdkLoaded ? 'pointer' : 'not-allowed', 
                opacity: loading ? 0.7 : 1, 
                width: '100%', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: '12px',
                boxShadow: '0 10px 15px -3px rgba(24, 119, 242, 0.3)'
              }}
            >
              {loading ? (
                <>Connecting to Meta...</>
              ) : (
                <>
                  <Icons.FaFacebook size={20} /> 
                  Continue with Facebook Login
                </>
              )}
            </button>
            
            {!isSdkLoaded && (
              <p style={{ marginTop: '15px', fontSize: '13px', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                <Icons.FaSpinner className="fa-spin" /> Loading secure Meta environment...
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbeddedSetup;